import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { CustomListingCard, PaginationLinks, NoDataFound } from '../../../components';
import { formatMoney } from '../../../util/currency';
import { useConfiguration } from '../../../context/configurationContext';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;
  const config = useConfiguration();
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(listing => {
          const id = listing.id.uuid;
          const { title, price, description, publicData } = listing.attributes || {};
          const location =
            publicData?.location?.address ||
            publicData?.location ||
            publicData?.city ||
            'No location';
          const imageAsset = listing.images?.[0] || null;
          const imageUrl =
            imageAsset?.attributes?.variants?.['listing-card']?.url ||
            imageAsset?.attributes?.variants?.['scaled-medium']?.url ||
            null;

          const formattedListing = {
            id,
            title,
            description,
            price:
              price && price.currency === config.currency
                ? formatMoney(intl, price)
                : '',
            category: publicData?.categoryLevel1 || publicData?.category || publicData?.listingType || 'Other',
            rating: 'New',
            seller:
              listing.author?.attributes?.profile?.displayName ||
              'Unknown Seller',
            location,
            usedOrNew: publicData?.usedOrNew || '',
            imageAsset,
            image: imageUrl,
            publicData,
          };

          return (
            <li key={id} className={css.resultItem}>
              <CustomListingCard
                className={css.listingCard}
                listing={formattedListing}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
              />
            </li>
          );
        })}
        {props.children}
      </ul>
      {listings.length === 0 && (
        <div style={{ padding: '40px 0', display: 'flex', justifyContent: 'center' }}>
          <NoDataFound description="No listings match this filter." />
        </div>
      )}
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
